"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { useSession } from "@/lib/auth/client"
import { useQueryClient } from "@tanstack/react-query"
import { LogOut, ShieldAlert } from "lucide-react"
import { toast } from "sonner"
import { adminUserService } from "@/services/api/admin"

const getHttpStatus = (error: unknown): number | undefined => {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return undefined
  }

  const response = (error as { response?: { status?: number } }).response
  return response?.status
}

export default function ImpersonationBanner() {
  const { data: session, update } = useSession()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [isEnding, setIsEnding] = useState(false)

  const impersonation = session?.impersonation || session?.user?.impersonation
  if (!impersonation) return null

  const targetLabel = impersonation.targetName || impersonation.targetEmail || impersonation.targetUserId
  const actorLabel = impersonation.actorEmail || impersonation.actorUserId
  const expiresAt = new Date(impersonation.expiresAt).toLocaleTimeString("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
  })

  const handleEnd = async () => {
    setIsEnding(true)
    try {
      await adminUserService.endCurrentImpersonation()
      await update({ impersonationAction: "end" })
      queryClient.clear()
      router.replace("/admin/users")
      router.refresh()
    } catch (error) {
      console.error("Failed to end impersonation", error)
      const status = getHttpStatus(error)
      if (status === 401 || status === 403) {
        await update({ impersonationAction: "end" })
        queryClient.clear()
        router.replace("/admin/users")
        router.refresh()
        return
      }

      toast.error("Impersonation oturumu bitirilemedi.")
    } finally {
      setIsEnding(false)
    }
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[70] md:left-auto md:right-4 md:w-[min(520px,calc(100vw-2rem))]">
      <div className="flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-100 px-3 py-2 text-sm text-amber-950 shadow-lg shadow-slate-900/10">
        <div className="flex min-w-0 items-start gap-2">
          <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div className="min-w-0">
            <div className="truncate font-semibold">{targetLabel} adına görüntülüyorsunuz</div>
            <div className="truncate text-xs text-amber-800">
              Admin: {actorLabel} · Bitiş: {expiresAt}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleEnd}
          disabled={isEnding}
          className="inline-flex h-8 flex-shrink-0 items-center gap-2 rounded-md border border-amber-400 bg-white px-3 text-xs font-semibold text-amber-900 transition-colors hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <LogOut className="h-3.5 w-3.5" />
          {isEnding ? "Bitiriliyor" : "Bitir"}
        </button>
      </div>
    </div>
  )
}
